import { apisData } from "@/data";
import type { WorkloadUserDetailInterface, WorkloadUserRowInterface } from "@/interfaces";
import { apiClient } from "@/lib/axios";

const normalizeWorkloadUser = (u: any): WorkloadUserRowInterface => ({
    user: {
        id: u.user_id ?? u.user?.id ?? "",
        name: u.user_name ?? u.user?.full_name ?? u.name ?? "",
    },
    total_tasks: u.total_tasks ?? 0,
    completed_tasks: u.completed_tasks ?? 0,
    in_progress_tasks: u.in_progress_tasks ?? 0,
    blocked_tasks: u.blocked_tasks ?? 0,
    total_story_points: u.story_points ?? u.total_story_points ?? 0,
    completed_story_points: u.completed_story_points ?? 0,
    total_estimated_hours: u.estimated_hours ?? u.total_estimated_hours ?? 0,
    logged_hours: u.logged_hours ?? 0,
});

const extractRows = (raw: any): WorkloadUserRowInterface[] => {
    const inner = raw?.data ?? raw;
    const users = inner?.by_user ?? inner?.data ?? (Array.isArray(inner) ? inner : []);
    return users.map(normalizeWorkloadUser);
};

export const workloadService = {
    byProjectHandler: async (projectId: string): Promise<WorkloadUserRowInterface[]> => {
        if (!projectId) throw new Error("Project ID is required");
        const { data } = await apiClient.get<any>(apisData.workload.byProject(projectId));
        return extractRows(data);
    },

    bySprintHandler: async (sprintId: string): Promise<WorkloadUserRowInterface[]> => {
        if (!sprintId) throw new Error("Sprint ID is required");
        const { data } = await apiClient.get<any>(apisData.workload.bySprint(sprintId));
        return extractRows(data);
    },

    byUserHandler: async (userId: string): Promise<WorkloadUserDetailInterface | null> => {
        if (!userId) throw new Error("User ID is required");
        const { data } = await apiClient.get<any>(apisData.workload.byUser(userId));
        const inner = data?.data ?? data;
        if (!inner || inner.total_tasks === undefined) return null;
        return {
            total_tasks: inner.total_tasks ?? 0,
            by_status: inner.by_status ?? {},
            by_sprint: (inner.by_sprint ?? []).map((s: any) => ({
                sprint_id: s.sprint_id,
                sprint_name: s.sprint_name,
                project_name: s.project_name,
                task_count: s.task_count ?? 0,
                story_points: s.story_points ?? 0,
            })),
            total_story_points: inner.total_story_points ?? 0,
            total_estimated_hours: inner.total_estimated_hours ?? 0,
        };
    },
};
